import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { FaRocket, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';

// Import components
import CompanySelection from './components/CompanySelection';
import Statistics from './components/Statistics';
import GeocodingControls from './components/GeocodingControls';
import PropertiesList from './components/PropertiesList';
import GeocodingResultsModal from './components/GeocodingResultsModal';

// Types
interface Company {
  id: number;
  name: string;
  business_name?: string;
}

interface Property {
  id: number;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  latitude: number | null;
  longitude: number | null;
  fk_business_id: number;
}

// Constants
const PROPERTIES_PER_PAGE = 5;

const Geocoding: React.FC = () => {
  const { t } = useTranslation();
  
  // Company selection state
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  
  // Property state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedProperties, setSelectedProperties] = useState<Set<number>>(new Set());
  const [selectionMode, setSelectionMode] = useState<string>('all-missing');
  const [isLoadingProperties, setIsLoadingProperties] = useState<boolean>(false);
  const [propertiesLoaded, setPropertiesLoaded] = useState<boolean>(false);
  const [modeAutoSwitched, setModeAutoSwitched] = useState<boolean>(false);
  const [showClearConfirm, setShowClearConfirm] = useState<boolean>(false);
  const [clearConfirmText, setClearConfirmText] = useState<string>('');
  const [showResultsModal, setShowResultsModal] = useState<boolean>(false);
  const [geocodingResults, setGeocodingResults] = useState<any[]>([]);
  const [geocodingDuration, setGeocodingDuration] = useState<string>('');

  // Fallback function for translations
  const getText = (key: string): string => {
    try {
      return t(key) || key.split('.').pop() || key;
    } catch (error) {
      return key.split('.').pop() || key;
    }
  };

  // Fetch properties query
  const {
    data: propertiesData,
    refetch: fetchProperties,
    isLoading: isLoadingPropertiesQuery
  } = useQuery(
    ['properties', selectedCompany?.id, currentPage],
    async () => {
      if (!selectedCompany?.id) return null;
      
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/property/missing-coordinates`, {
        params: {
          companyId: selectedCompany.id,
          page: currentPage,
          limit: PROPERTIES_PER_PAGE
        }
      });
      
      return response.data;
    },
    {
      enabled: !!selectedCompany?.id,
      retry: false
    }
  );

  // Bulk geocoding mutation
  const bulkGeocodeMutation = useMutation(
    async (data: { companyId: number; batchSize: number; delayMs: number }) => {
      const startTime = Date.now();
      const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/geocoding/bulk-geocode`, data);
      
      // Calculate duration
      const duration = Date.now() - startTime;
      const durationText = duration < 1000 ? `${duration}ms` : `${(duration / 1000).toFixed(1)}s`;
      setGeocodingDuration(durationText);
      
      return response.data;
    },
    {
      onSuccess: (data) => {
        console.log('Geocoding completed:', data);
        
        // Extract results from the response
        if (data.results) {
          setGeocodingResults(data.results);
        } else {
          // If no results in response, create a basic structure
          setGeocodingResults([]);
        }
        
        // Show results modal
        setShowResultsModal(true);
        
        // Refresh properties after successful geocoding
        fetchProperties();
      },
      onError: (error) => {
        console.error('Geocoding failed:', error);
      }
    }
  );

  // Clear coordinates mutation
  const clearCoordinatesMutation = useMutation(
    async (data: { companyId: number; confirmation: string }) => {
      const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/super-admin/geocoding/clear-coordinates`, data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        console.log('Coordinates cleared:', data);
        // Refresh properties after clearing coordinates
        fetchProperties();
      },
      onError: (error) => {
        console.error('Failed to clear coordinates:', error);
      }
    }
  );

  // Auto-select missing coordinate properties when they're loaded and mode is "all-missing"
  React.useEffect(() => {
    if (selectionMode === 'all-missing' && propertiesData?.data && propertiesData.data.length > 0) {
      // Auto-select only the properties that are missing coordinates
      const missingCoordinateIds = propertiesData.data
        .filter((p: Property) => !p.latitude || !p.longitude)
        .map((p: Property) => p.id);
      
      setSelectedProperties(new Set(missingCoordinateIds));
    }
  }, [propertiesData?.data, propertiesData?.missingCoordinates, selectionMode]);

  // Company selection handlers
  const handleCompanySelect = (company: Company): void => {
    setSelectedCompany(company);
    setSelectedProperties(new Set());
    setCurrentPage(1);
    setPropertiesLoaded(false);
    setSelectionMode('all-missing'); // Auto-select "All Missing Coordinates" mode
  };

  const handleClearCompanySelection = (): void => {
    setSelectedCompany(null);
    setSelectedProperties(new Set());
    setPropertiesLoaded(false);
    setCurrentPage(1);
  };

  // Handle selection mode change
  const handleSelectionModeChange = (mode: string): void => {
    setSelectionMode(mode);
    setModeAutoSwitched(false); // Clear auto-switched flag when user manually changes mode
    
    if (mode === 'all-missing') {
      // Auto-select only missing coordinate properties
      if (propertiesData?.data && propertiesData.data.length > 0) {
        const missingCoordinateIds = propertiesData.data
          .filter((p: Property) => !p.latitude || !p.longitude)
          .map((p: Property) => p.id);
        setSelectedProperties(new Set(missingCoordinateIds));
      }
    } else {
      // Clear selections for other modes
      setSelectedProperties(new Set());
    }
  };

  // Handle property selection
  const handlePropertyToggle = (propertyId: number): void => {
    const newSelected = new Set(selectedProperties);
    if (newSelected.has(propertyId)) {
      newSelected.delete(propertyId);
    } else {
      newSelected.add(propertyId);
    }
    setSelectedProperties(newSelected);
    
    // Auto-switch to "Selected Only" mode when user manually changes selection
    if (selectionMode === 'all-missing') {
      setSelectionMode('selected-only');
      setModeAutoSwitched(true);
      console.log('🔄 Mode automatically switched to "Selected Only" due to manual selection change');
      
      // Clear the flag after a few seconds
      setTimeout(() => setModeAutoSwitched(false), 5000);
    }
  };

  // Handle select all/deselect all
  const handleSelectAll = (): void => {
    if (propertiesData?.data) {
      // Select only properties that are missing coordinates
      const missingCoordinateIds = propertiesData.data
        .filter((p: Property) => !p.latitude || !p.longitude)
        .map((p: Property) => p.id);
      
      setSelectedProperties(new Set(missingCoordinateIds));
      
      // Auto-switch to "Selected Only" mode when using Select All
      if (selectionMode === 'all-missing') {
        setSelectionMode('selected-only');
        setModeAutoSwitched(true);
        setTimeout(() => setModeAutoSwitched(false), 5000);
      }
    }
  };

  const handleDeselectAll = (): void => {
    setSelectedProperties(new Set());
    
    // Auto-switch to "Selected Only" mode when using Deselect All
    if (selectionMode === 'all-missing') {
      setSelectionMode('selected-only');
      setModeAutoSwitched(true);
      setTimeout(() => setModeAutoSwitched(false), 5000);
    }
  };

  // Handle page change
  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
    
    // If in "all-missing" mode, auto-select missing coordinates on the new page
    if (selectionMode === 'all-missing') {
      // We'll auto-select when the new page data loads via useEffect
    } else {
      // For other modes, clear selections on page change
      setSelectedProperties(new Set());
    }
  };

  // Calculate statistics
  const totalProperties = propertiesData?.total || 0;
  const geocodedCount = propertiesData?.geocoded || 0;
  const missingCoordinatesCount = propertiesData?.missingCoordinates || 0; // Use the missingCoordinates count from API
  const successRate = totalProperties > 0 ? Math.round((geocodedCount / totalProperties) * 100) : 0;

  // Calculate properties to geocode
  const getPropertiesToGeocode = (): number => {
    const result = (() => {
      switch (selectionMode) {
        case 'all-missing':
          return missingCoordinatesCount; // Total missing coordinates across all pages
        case 'selected-only':
          return selectedProperties.size; // Only manually selected properties
        default:
          return selectedProperties.size; // Default to selected properties
      }
    })();
    
    return result;
  };

  const propertiesToGeocode = getPropertiesToGeocode();

  // Handle start geocoding
  const handleStartGeocoding = async (): Promise<void> => {
    if (!selectedCompany?.id || propertiesToGeocode === 0) return;

    try {
      await bulkGeocodeMutation.mutateAsync({
        companyId: selectedCompany.id,
        batchSize: 10,
        delayMs: 1000
      });
    } catch (error) {
      console.error('Failed to start geocoding:', error);
    }
  };

  // Handle clear coordinates
  const handleClearCoordinates = (): void => {
    if (!selectedCompany?.id) return;
    setShowClearConfirm(true);
    setClearConfirmText('');
  };

  const handleConfirmClearCoordinates = async (): Promise<void> => {
    if (!selectedCompany?.id) return;
    
    const confirmation = `CLEAR_COORDINATES_${selectedCompany.id}`;
    
    // Debug logging
    console.log('🔍 Clear coordinates debug:', {
      selectedCompany,
      companyId: selectedCompany.id,
      companyIdType: typeof selectedCompany.id,
      confirmation,
      requestBody: {
        companyId: selectedCompany.id,
        confirmation: confirmation
      }
    });
    
    try {
      await clearCoordinatesMutation.mutateAsync({
        companyId: selectedCompany.id,
        confirmation: confirmation
      });
      setShowClearConfirm(false);
      setClearConfirmText('');
    } catch (error) {
      console.error('Failed to clear coordinates:', error);
    }
  };

  const handleCancelClearCoordinates = (): void => {
    setShowClearConfirm(false);
    setClearConfirmText('');
  };

  // Handle load properties
  const handleLoadProperties = async (): Promise<void> => {
    if (!selectedCompany?.id) return;
    
    setIsLoadingProperties(true);
    try {
      setSelectionMode('all-missing'); // Ensure we're in "all-missing" mode
      await fetchProperties();
      setPropertiesLoaded(true);
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setIsLoadingProperties(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
              <FaRocket size={24} className="text-white" />
            </div>
    <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {getText('Property Geocoding')}
              </h1>
              <p className="text-gray-600 mt-1">
                Manage and update property coordinates using Mapbox geocoding
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Company Selection & Statistics */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Company Selection */}
            <CompanySelection
              selectedCompany={selectedCompany}
              onCompanySelect={handleCompanySelect}
              onClearSelection={handleClearCompanySelection}
              onLoadProperties={handleLoadProperties}
              isLoadingProperties={isLoadingProperties}
              propertiesLoaded={propertiesLoaded}
              propertiesData={propertiesData}
            />

            {/* Statistics */}
            <Statistics
              totalProperties={totalProperties}
              geocodedCount={geocodedCount}
              missingCoordinatesCount={missingCoordinatesCount}
              successRate={successRate}
            />
          </div>

          {/* Right Column - Geocoding Controls & Properties */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Geocoding Controls */}
            <GeocodingControls
              selectionMode={selectionMode}
              onSelectionModeChange={handleSelectionModeChange}
              selectedPropertiesCount={selectedProperties.size}
              totalPropertiesCount={propertiesData?.data?.length || 0}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              propertiesToGeocode={propertiesToGeocode}
              onStartGeocoding={handleStartGeocoding}
              isGeocoding={bulkGeocodeMutation.isLoading}
              missingCoordinatesCount={missingCoordinatesCount}
              modeAutoSwitched={modeAutoSwitched}
              onClearCoordinates={handleClearCoordinates}
              isClearingCoordinates={clearCoordinatesMutation.isLoading}
              selectedCompany={selectedCompany}
            />

            {/* Properties List */}
            <PropertiesList
              properties={propertiesData?.data || []}
              selectedProperties={Array.from(selectedProperties)}
              onPropertyToggle={handlePropertyToggle}
              currentPage={currentPage}
              totalPages={Math.ceil((propertiesData?.data?.length || 0) / 5)}
              onPageChange={setCurrentPage}
              onViewResults={() => setShowResultsModal(true)}
              hasResults={geocodingResults.length > 0}
            />
          </div>
        </div>
      </div>

      {/* Clear Coordinates Confirmation Dialog */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <FaExclamationTriangle size={20} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Clear Coordinates</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                This will permanently clear <strong>ALL coordinates</strong> for company <strong>{selectedCompany?.name}</strong> (ID: {selectedCompany?.id}).
              </p>
              <p className="text-gray-700 mb-4">
                This action cannot be undone and will affect all properties in this company.
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800 font-medium mb-2">To confirm, type exactly:</p>
                <code className="text-red-900 bg-red-100 px-2 py-1 rounded text-sm font-mono">
                  CLEAR_COORDINATES_{selectedCompany?.id}
                </code>
              </div>
            </div>
            
            <div className="mb-4">
              <input
                type="text"
                value={clearConfirmText}
                onChange={(e) => setClearConfirmText(e.target.value)}
                placeholder="Type the confirmation text above"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelClearCoordinates}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmClearCoordinates}
                disabled={clearConfirmText !== `CLEAR_COORDINATES_${selectedCompany?.id}`}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                Clear All Coordinates
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Geocoding Results Modal */}
      <GeocodingResultsModal
        isOpen={showResultsModal}
        onClose={() => setShowResultsModal(false)}
        results={geocodingResults}
        properties={propertiesData?.data || []}
        companyName={selectedCompany?.name || 'Unknown Company'}
        totalDuration={geocodingDuration}
      />
    </div>
  );
};

export default Geocoding;
